// src/pages/BotPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
    getBotSettings,
    updateBotSettings,
    getBotStatus,
    getBotLogs,
    runBotTick,
    killSwitch,
} from "../api/botApi";
import { getAuthToken } from "../apiClient";

const TABS = ["Bot Control", "Risk Level", "Live Status", "Logs", "Kill Switch"];

function getUserIdFromToken() {
    try {
        const token = getAuthToken();
        if (!token) return "";
        const parts = token.split(".");
        if (parts.length < 2) return "";
        const payload = JSON.parse(atob(parts[1]));
        return (payload?.email || "").toLowerCase();
    } catch (e) {
        return "";
    }
}

function Pill({ children }) {
    return (
        <span className="px-3 py-1 rounded-full text-xs bg-gray-800 border border-gray-700">
            {children}
        </span>
    );
}

export default function BotPage() {
    const [active, setActive] = useState("Bot Control");
    const [settings, setSettings] = useState(null);
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const userId = getUserIdFromToken();

    async function refreshAll() {
        if (!userId) {
            setError("Missing user session. Please log in again.");
            return;
        }
        setError("");
        try {
            const s = await getBotSettings(userId);
            setSettings(s);
            const st = await getBotStatus(userId);
            setStatus(st);
            const lg = await getBotLogs(userId, 200);
            setLogs(lg.logs || []);
        } catch (e) {
            setError(e.message || String(e));
        }
    }

    useEffect(() => {
        refreshAll();
    }, [userId]);

    const profiles = useMemo(() => settings?.profiles || {}, [settings]);
    const supportedBrokers = useMemo(() => settings?.supported_brokers || [], [settings]);

    async function patch(p) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            const s = await updateBotSettings(userId, p);
            setSettings(s);
            const st = await getBotStatus(userId);
            setStatus(st);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function runOneTick() {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await runBotTick(userId);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    async function doKill(disable = true) {
        if (!userId) return;
        setBusy(true);
        setError("");
        try {
            await killSwitch(userId, !disable);
            await refreshAll();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setBusy(false);
        }
    }

    const enabled = Boolean(settings?.enabled);

    return (
        <div className="flex gap-4">
            <div className="w-56 shrink-0">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-2">
                    <div className="text-sm font-semibold mb-2">AI Bot</div>
                    <div className="flex flex-col gap-1">
                        {TABS.map((t) => (
                            <button
                                key={t}
                                onClick={() => setActive(t)}
                                className={`text-left px-3 py-2 rounded-lg text-sm ${active === t ? "bg-gray-800 border border-gray-700" : "hover:bg-gray-800/50"
                                    }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    <div className="mt-4 flex flex-col gap-2">
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Status</span>
                            <Pill>{enabled ? "ON" : "OFF"}</Pill>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Broker</span>
                            <Pill>{settings?.broker || "-"}</Pill>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-300">
                            <span>Risk</span>
                            <Pill>{settings?.risk_level || "-"}</Pill>
                        </div>
                    </div>
                </div>

                {error ? <div className="mt-3 text-red-400 text-sm">{error}</div> : null}
            </div>

            <div className="flex-1">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="text-lg font-semibold">{active}</div>
                            <div className="text-xs text-gray-400">
                                When Bot Lock is ON, manual trading endpoints should return 409 to prevent conflicts.
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                disabled={busy}
                                onClick={refreshAll}
                                className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
                            >
                                Refresh
                            </button>
                            <button
                                disabled={busy}
                                onClick={runOneTick}
                                className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm"
                            >
                                Run 1 Tick
                            </button>
                        </div>
                    </div>

                    <div className="mt-4">
                        {active === "Bot Control" && (
                            <BotControl
                                settings={settings}
                                busy={busy}
                                supportedBrokers={supportedBrokers}
                                patch={patch}
                            />
                        )}
                        {active === "Risk Level" && (
                            <RiskLevel
                                settings={settings}
                                profiles={profiles}
                                busy={busy}
                                patch={patch}
                            />
                        )}
                        {active === "Live Status" && <LiveStatus status={status} />}
                        {active === "Logs" && <Logs logs={logs} />}
                        {active === "Kill Switch" && (
                            <KillSwitch enabled={enabled} busy={busy} doKill={doKill} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-800">
            <div className="text-sm text-gray-300">{label}</div>
            <div>{children}</div>
        </div>
    );
}

function Toggle({ value, onChange, disabled }) {
    return (
        <button
            disabled={disabled}
            onClick={() => onChange(!value)}
            className={`px-3 py-2 rounded-lg border text-sm ${value ? "bg-green-900/30 border-green-700" : "bg-gray-800 border-gray-700"
                }`}
        >
            {value ? "ON" : "OFF"}
        </button>
    );
}

function BotControl({ settings, busy, supportedBrokers, patch }) {
    if (!settings) return <div className="text-sm text-gray-400">Loading…</div>;
    return (
        <div className="space-y-2">
            <Row label="Bot Enabled">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.enabled)}
                    onChange={(v) => patch({ enabled: v })}
                />
            </Row>

            <Row label="Broker">
                <select
                    disabled={busy}
                    value={settings.broker}
                    onChange={(e) => patch({ broker: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                >
                    {supportedBrokers.map((b) => (
                        <option key={b} value={b}>
                            {b}
                        </option>
                    ))}
                </select>
            </Row>

            <Row label="Scan All NYSE/Nasdaq">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.scan_all)}
                    onChange={(v) => patch({ scan_all: v })}
                />
            </Row>

            <Row label="Bot Lock Account (block manual trading)">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.lock_account)}
                    onChange={(v) => patch({ lock_account: v })}
                />
            </Row>

            <Row label="Paper Mode">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.paper_mode)}
                    onChange={(v) => patch({ paper_mode: v })}
                />
            </Row>

            <Row label="Allow Live Execution (extra gate)">
                <Toggle
                    disabled={busy}
                    value={Boolean(settings.allow_live)}
                    onChange={(v) => patch({ allow_live: v })}
                />
            </Row>

            <div className="text-xs text-gray-400 mt-3">
                Note: Robinhood + E*TRADE will show in the dropdown, but execution will error until their OAuth/2FA is wired.
            </div>
        </div>
    );
}

function RiskLevel({ settings, profiles, busy, patch }) {
    if (!settings) return <div className="text-sm text-gray-400">Loading…</div>;

    const keys = Object.keys(profiles || {});
    const selected = profiles?.[settings.risk_level];

    return (
        <div>
            <Row label="Risk Profile">
                <select
                    disabled={busy}
                    value={settings.risk_level}
                    onChange={(e) => patch({ risk_level: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                >
                    {keys.map((k) => (
                        <option key={k} value={k}>
                            {profiles[k].label}
                        </option>
                    ))}
                </select>
            </Row>

            <div className="mt-4 bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Profile Details</div>
                {selected ? (
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                        {JSON.stringify(selected, null, 2)}
                    </pre>
                ) : (
                    <div className="text-xs text-gray-500">No profile selected.</div>
                )}
            </div>
        </div>
    );
}

function LiveStatus({ status }) {
    if (!status) return <div className="text-sm text-gray-400">Loading…</div>;
    const st = status.state || {};
    const s = status.settings || {};

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Settings</div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(s, null, 2)}
                </pre>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">State</div>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(st, null, 2)}
                </pre>
            </div>
        </div>
    );
}

function Logs({ logs }) {
    return (
        <div className="space-y-2">
            <div className="text-xs text-gray-400">Newest first</div>
            <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden">
                <div className="max-h-[520px] overflow-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-900">
                            <tr className="text-left">
                                <th className="p-2 border-b border-gray-800">Time</th>
                                <th className="p-2 border-b border-gray-800">Level</th>
                                <th className="p-2 border-b border-gray-800">Event</th>
                                <th className="p-2 border-b border-gray-800">Symbol</th>
                                <th className="p-2 border-b border-gray-800">Payload</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(logs || []).map((l) => (
                                <tr key={l.id} className="border-b border-gray-900">
                                    <td className="p-2 text-gray-300">{l.ts}</td>
                                    <td className="p-2 text-gray-300">{l.level}</td>
                                    <td className="p-2 text-gray-200">{l.event}</td>
                                    <td className="p-2 text-gray-200">{l.symbol}</td>
                                    <td className="p-2 text-gray-400">
                                        <div className="max-w-[520px] truncate">{l.payload_json}</div>
                                    </td>
                                </tr>
                            ))}
                            {!logs?.length ? (
                                <tr>
                                    <td className="p-3 text-gray-500" colSpan={5}>
                                        No logs yet.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function KillSwitch({ enabled, busy, doKill }) {
    return (
        <div className="space-y-3">
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <div className="text-sm font-semibold mb-2">Emergency Stop</div>
                <div className="text-xs text-gray-400 mb-3">
                    Disables bot execution immediately. (Liquidation is not included in this MVP—add it later once your broker adapters are fully validated.)
                </div>
                <button
                    disabled={busy || !enabled}
                    onClick={() => doKill(true)}
                    className="px-4 py-2 rounded-lg bg-red-900/40 border border-red-700 text-sm"
                >
                    Kill Switch (Disable Bot)
                </button>
            </div>
        </div>
    );
}